import { Link as InertiaLink, useForm } from '@inertiajs/react';
import GoogleIcon from '@mui/icons-material/Google';
import { Box, Divider } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import ArrowRight from '../../svgIcons/ArrowRight';
import AuthButton from './AuthButton';
import Layout from './Layout';

/**
 * @see \App\Http\Controllers\AuthController::viewLogin
 * @see app/Http/Controllers/AuthController.php:121
 * @route 'auth/login'
 * @method get
 * @name 'login'
 */
function Login() {
  const { data, setData, post, processing, errors } = useForm({
    email: '',
    password: '',
    remember: false,
  });

  return (
    <Box
      component={'form'}
      onSubmit={(e) => {
        e.preventDefault();
        post('/auth/login');
      }}
      noValidate
      id="login-form"
      sx={{
        width: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            marginBottom: 1,
          }}
        >
          Welcome back
        </Typography>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: '0.95rem',
          }}
        >
          Enter your credentials to access your account
        </Typography>
      </Box>

      {/* Email Field */}
      <Box sx={{ marginBottom: 2.5 }}>
        <Typography
          component="label"
          htmlFor="email"
          sx={{
            display: 'block',
            fontWeight: 500,
            fontSize: '0.875rem',
            marginBottom: 1,
            color: 'text.primary',
          }}
        >
          Email address
        </Typography>
        <TextField
          variant="outlined"
          placeholder="name@company.com"
          required
          type="email"
          name="email"
          id="email"
          value={data.email}
          error={!!errors.email}
          helperText={errors?.email}
          fullWidth
          onChange={(e) => setData('email', e.target.value)}
          slotProps={{
            input: {
              inputProps: {
                'aria-label': 'Email Address',
              },
            },
          }}
        />
      </Box>

      {/* Password Field */}
      <Box sx={{ marginBottom: 2 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: 1 }}
        >
          <Grid>
            <Typography
              component="label"
              htmlFor="password"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.primary',
              }}
            >
              Password
            </Typography>
          </Grid>
          <Grid>
            <Link
              component={InertiaLink}
              href="/auth/forgot-password"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              Forgot password?
            </Link>
          </Grid>
        </Grid>
        <TextField
          variant="outlined"
          placeholder="Enter your password"
          required
          type="password"
          name="password"
          id="password"
          value={data.password}
          error={!!errors.password}
          helperText={errors?.password}
          fullWidth
          onChange={(e) => setData('password', e.target.value)}
          slotProps={{
            input: {
              inputProps: {
                'aria-label': 'Password',
              },
            },
          }}
        />
      </Box>

      {/* Remember Me */}
      <FormControl
        fullWidth
        sx={{ marginBottom: 3 }}
        error={Boolean(errors.remember)}
      >
        <FormControlLabel
          label="Keep me logged in"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              color: 'text.secondary',
            },
          }}
          control={
            <Checkbox
              checked={data.remember}
              onChange={(e) => setData('remember', e.target.checked)}
              size="small"
              sx={{
                '&.MuiCheckbox-root': {
                  padding: '6px',
                },
              }}
            />
          }
        />
      </FormControl>

      {/* Sign In Button */}
      <AuthButton
        id="login"
        disabled={processing || !data.email || !data.password}
        color="primary"
        variant="contained"
        type="submit"
        form="login-form"
        fullWidth
        size={'large'}
        endIcon={
          <Icon>
            <ArrowRight />
          </Icon>
        }
        sx={{
          py: 1.5,
        }}
      >
        Sign in
      </AuthButton>

      {/* Divider */}
      <Box my={3}>
        <Divider>
          <Typography
            sx={{ color: 'text.secondary', fontSize: '0.875rem', px: 2 }}
          >
            Or continue with
          </Typography>
        </Divider>
      </Box>

      {/* Google Button */}
      <Button
        fullWidth
        variant="outlined"
        color="inherit"
        startIcon={<GoogleIcon />}
        href={route('auth.google')}
        sx={{ py: 1.25 }}
      >
        Continue with Google
      </Button>

      {/* Sign Up Link */}
      <Box sx={{ textAlign: 'center', marginTop: 3 }}>
        <Typography
          component="span"
          sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
        >
          Don't have an account?{' '}
        </Typography>
        <Link
          component={InertiaLink}
          href="/auth/register"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          Sign up
        </Link>
      </Box>
    </Box>
  );
}

Login.layout = (page: ReactNode) => <Layout>{page}</Layout>;

export default Login;
